'use client';
import { useEffect, useState } from 'react';
import clsx from 'clsx/lite';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { ELButton, ELIconButton } from '@davincihealthcare/elty-design-system-react';
import Location from './location';
import { ROUTES } from '@/routes';
import { useRouter, useSearchParams } from 'next/navigation';
import { getStringFromQueryParam } from '@/utils/string';
import { ModalSearchContainer } from '@/app/_components/searchbar/ModalSearchContainer';
import HealthcareService from '@/app/_components/searchbar/healthcareService';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';

type SearchbarProps = {
  inline?: boolean;
  asPlaceholder?: boolean;
  defaultService?: string | null;
  keepQueryParams?: boolean;
  className?: string;
  idPrefix: string;
  searchOnOptionClick?: boolean;
  openModalOnMobile?: boolean;
  defaultLocation?: string;
};

const focusAutocompleteElement = (divId: string) => {
  const div = document.getElementById(`${divId}-input`) as HTMLDivElement;
  if (div) {
    const focusableElements = div.querySelectorAll<HTMLElement>('input');
    if (focusableElements.length === 0) return;
    focusableElements[0]?.focus();
    focusableElements[0]?.click();
  }
};

const Searchbar = ({
  idPrefix,
  inline = false,
  asPlaceholder = false,
  className,
  defaultService,
  keepQueryParams = false,
  searchOnOptionClick = false,
  openModalOnMobile = false,
  defaultLocation,
}: SearchbarProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const latQueryParam = getStringFromQueryParam(searchParams?.get('lat') ?? undefined);
  const lngQueryParam = getStringFromQueryParam(searchParams?.get('lng') ?? undefined);
  const locationQueryParam = getStringFromQueryParam(searchParams?.get('location') ?? undefined);
  const isDesktopAndSmaller = useIsDesktop();
  const [isServiceSearchModalVisible, setIsServiceSearchModalVisible] = useState(false);
  const [isLocationSearchModalVisible, setIsLocationSearchModalVisible] = useState(false);

  const healthcareInputId = `${idPrefix}-healthcare`;
  const locationInputId = `${idPrefix}-location`;

  useEffect(() => {
    if (isDesktopAndSmaller) {
      if (isServiceSearchModalVisible) focusAutocompleteElement(healthcareInputId);
      if (isLocationSearchModalVisible) focusAutocompleteElement(locationInputId);
    }
  }, [isDesktopAndSmaller, isServiceSearchModalVisible, healthcareInputId, isLocationSearchModalVisible, locationInputId]);

  const [healthcareService, setHealthcareService] = useState<string>(() => {
    // example page prenota-visita/visita-dermatologica defaultService is "visita-dermatologica"
    if (typeof defaultService === 'string') return defaultService;
    return '';
  });

  const [location, setLocation] = useState<string>(defaultLocation || locationQueryParam || '');
  const [locationCoordinates, setLocationCoordinates] = useState<number[] | null>(null);

  // example page cerca?service=visita-dermatologica
  const searchParam = getStringFromQueryParam(searchParams?.get('service') ?? undefined);

  useEffect(() => {
    setLocationCoordinates(latQueryParam && lngQueryParam ? [Number(latQueryParam), Number(lngQueryParam)] : null);
    if (locationQueryParam) setLocation(locationQueryParam);
  }, [latQueryParam, lngQueryParam, locationQueryParam]);

  useEffect(() => {
    if (searchParam) setHealthcareService(searchParam);
  }, [searchParam]);

  const search = ({
    newService,
    newLocation,
  }: { newService?: { value: string }; newLocation?: { value: string; coordinates?: number[] | undefined } } = {}) => {
    const serviceToSearch = newService?.value !== undefined ? newService.value : healthcareService;
    const locationToSearch = newLocation?.value !== undefined ? newLocation.value : location;

    if (!serviceToSearch && !locationToSearch) {
      return;
    }

    const newParams = new URLSearchParams(keepQueryParams ? searchParams?.toString() : '');

    const lat = newLocation?.coordinates?.length ? newLocation.coordinates[0] : locationCoordinates?.[0];
    const lng = newLocation?.coordinates?.length ? newLocation.coordinates[1] : locationCoordinates?.[1];

    newParams.delete('page');
    newParams.delete('userGeolocationActive');
    newParams.delete('order');
    newParams.set('service', serviceToSearch);

    if (locationToSearch) {
      newParams.set('location', locationToSearch);
      newParams.set('lat', lat ? `${lat}` : '');
      newParams.set('lng', lng ? `${lng}` : '');
    } else {
      newParams.delete('lat');
      newParams.delete('lng');
      newParams.delete('location');
    }

    router.push(`${ROUTES.BOOKING.pathname}?${newParams.toString()}`);
  };

  return (
    <div className={clsx('relative flex items-center', inline ? 'h-10 rounded-6xl bg-gray-100' : 'flex-col gap-y-5', className)}>
      <ModalSearchContainer
        isModalOpen={isServiceSearchModalVisible}
        onClose={() => {
          setIsServiceSearchModalVisible(false);
        }}
      >
        <HealthcareService
          value={healthcareService}
          setValue={setHealthcareService}
          inline={inline}
          showCurrentTextInOptions={searchOnOptionClick}
          onOptionClick={option => {
            if (searchOnOptionClick) {
              search({ newService: option });
            }
            setHealthcareService(option.value);
            setIsServiceSearchModalVisible(false);
          }}
          id={healthcareInputId}
          onClick={() => {
            if (isDesktopAndSmaller && !isServiceSearchModalVisible && openModalOnMobile) {
              setIsServiceSearchModalVisible(true);
            }
          }}
        />
      </ModalSearchContainer>

      {inline && (
        <div
          className={inline ? 'relative h-6 w-0.5 bg-[#627282]' : 'hidden md:relative md:block md:h-10 md:w-px md:bg-[#627282] lg:h-12'}
        />
      )}

      <ModalSearchContainer
        isModalOpen={isLocationSearchModalVisible}
        onClose={() => {
          setIsLocationSearchModalVisible(false);
        }}
      >
        <Location
          value={location}
          setValue={setLocation}
          setLocationCoordinates={setLocationCoordinates}
          inline={inline}
          onOptionClick={option => {
            if (searchOnOptionClick) {
              search({ newLocation: option });
            }
            setLocation(option.value);
            setIsLocationSearchModalVisible(false);
          }}
          id={locationInputId}
          onClick={() => {
            if (isDesktopAndSmaller && !isLocationSearchModalVisible && openModalOnMobile) {
              setIsLocationSearchModalVisible(true);
            }
          }}
        />
      </ModalSearchContainer>

      {/* desktop */}
      {inline && !asPlaceholder && (
        <div className="mr-1.5">
          <ELIconButton aria-label="cerca" variant="filled" color="secondary" onClick={() => search()} size="medium">
            <MagnifyingGlassIcon />
          </ELIconButton>
        </div>
      )}
      {/* mobile collapsible menu */}
      {!inline && !asPlaceholder && (
        <ELButton
          variant="filled"
          color="secondary"
          onClick={() => search()}
          label="CERCA"
          leadingIcon={<MagnifyingGlassIcon />}
          fullWidth
          data-cy="button-searchbar"
        />
      )}
    </div>
  );
};

export default Searchbar;
